import { useCallback, useEffect, useState } from 'react'
import { Box } from '@mui/system'
import { useMediaQuery, Typography, Button, CircularProgress } from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import LoginBackSvg from '../../../images/loginBackSvg.svg'
import LoginRightImage from '../../../images/loginRightImage.svg'
import OfoqTextField from '../../../components/Forms/OfoqTextField'
import OfoqPasswordField from '../../../components/Forms/OfoqPasswordField'
import OfoqThemeBtn from '../../../components/Buttons/OfoqThemeBtn'
import {
  userAuthLogin,
  userAuthResetAlertHandler,
  userAuthResolveSsoByDomain,
} from '../../../redux/features/userAuth/userAuthThunk'
import { userAuthLoginWithVault } from '../../../redux/features/userAuth/biometricThunk'
import { selectAuthUser } from '../../../redux/features/userAuth/userAuthSlice'
import { isNotEmpty } from '../../../utils/helpers'
import AppRoutes from '../../../services/AppRoutes'
import CookiesStorageService from '../../../services/CookiesStorageService'
import ResponsiveAppBar from '../../../components/ResponsiveAppBar'
import LinkBtn from '../../../components/Buttons/LinkBtn'
import SwitchLanguageButton from '../../../components/Buttons/SwitchLanguageButton'
import UseTranslation from '../../../hooks/UseTranslation'
import { getTenantAssets } from '../../../utils/tenantHelper'
import useBiometricCapability from '../../../hooks/useBiometricCapability'
import LoginStepIndicator from './LoginStepIndicator'
import BiometricLoginButton from './BiometricLoginButton'
import {
  getEmailContinueValidationMessage,
  getPasswordValidationMessage,
  LOGIN_STEP,
} from './loginHelpers'
import LoaderWithErrorHandler from '../../../components/PageHelpers/LoaderWithErrorHandler'

const Index = () => {
  const isXsScreen = useMediaQuery((theme) => theme.breakpoints.only('xs'))
  const dispatch = useDispatch()
  const history = useHistory()
  const userAuth = useSelector(selectAuthUser)
  const { t } = UseTranslation()
  const { logoUrl } = getTenantAssets()

  const [step, setStep] = useState(LOGIN_STEP.EMAIL)
  const [email, setEmail] = useState(null)
  const [confirmedEmail, setConfirmedEmail] = useState('')
  const [password, setPassword] = useState(null)
  const [emailSubmitError, setEmailSubmitError] = useState(null)
  const [passwordSubmitError, setPasswordSubmitError] = useState(null)
  const [biometricUnlockLoading, setBiometricUnlockLoading] = useState(false)
  const biometricCapability = useBiometricCapability()

  useEffect(() => {
    if (isNotEmpty(CookiesStorageService.getAccessToken())) {
      const isFirstLogin = CookiesStorageService.getIsFirstLogin()
      const isAcceptedPolicies = CookiesStorageService.isAcceptedPolicies()

      if (isFirstLogin) {
        history.push(AppRoutes.firstTimePassword)
      } else if (!isAcceptedPolicies) {
        history.push(AppRoutes.consent)
      } else {
        history.push(AppRoutes.landing)
      }
    }
  }, [userAuth, history])

  const handleBiometricLogin = useCallback(async () => {
    setBiometricUnlockLoading(true)
    await dispatch(
      userAuthLoginWithVault({ reason: t?.['Log in to Orgarise'] || 'Log in to Orgarise' }),
    )
    setBiometricUnlockLoading(false)
  }, [dispatch, t])

  const handleChangeEmail = useCallback(() => {
    setStep(LOGIN_STEP.EMAIL)
    setPassword(null)
    setEmailSubmitError(null)
    dispatch(userAuthResetAlertHandler())
  }, [dispatch])

  const handleEmailContinue = useCallback(
    async (e) => {
      e?.preventDefault?.()
      if (userAuth?.loading) return

      const raw = email
      const validationMessage = getEmailContinueValidationMessage(raw, t)
      setEmailSubmitError(validationMessage || null)
      if (validationMessage) return

      const trimmedEmail = raw.trim()

      const result = await dispatch(userAuthResolveSsoByDomain({ email: trimmedEmail })).unwrap()

      if (result?.flow === 'PASSWORD') {
        setConfirmedEmail(trimmedEmail)
        setStep(LOGIN_STEP.PASSWORD)
        setPasswordSubmitError(null)
      }
    },
    [dispatch, email, t, userAuth?.loading],
  )

  const handlePasswordSubmit = useCallback(
    (e) => {
      e?.preventDefault?.()
      if (userAuth?.loading) return

      const validationMessage = getPasswordValidationMessage(password, t)
      setPasswordSubmitError(validationMessage || null)
      if (validationMessage) return

      dispatch(
        userAuthLogin({
          queries: {
            email: confirmedEmail,
            password,
          },
        }),
      )
    },
    [confirmedEmail, dispatch, password, t, userAuth?.loading],
  )

  const handleFormSubmit = useCallback(
    (e) => {
      if (step === LOGIN_STEP.EMAIL) {
        handleEmailContinue(e)
      } else {
        handlePasswordSubmit(e)
      }
    },
    [handleEmailContinue, handlePasswordSubmit, step],
  )

  const stepperIndex = step === LOGIN_STEP.PASSWORD ? 1 : 0

  const onEmailChange = (value) => {
    setEmail(value)

    const validationMessage = getEmailContinueValidationMessage(value, t)
    setEmailSubmitError(validationMessage || null)
  }

  const onPasswordChange = (value) => {
    setPassword(value)
  }

  return (
    <Box sx={styles.root}>
      {isXsScreen && <ResponsiveAppBar onlyLogo />}
      <Box sx={styles.loginContainer}>
        <Box sx={styles.leftSideContainer}>
          <Box sx={styles.titleContainer}>
            <Typography variant="h4" sx={styles.formTitle}>
              {`${t?.['Login to']}`}
              <Box component="img" sx={styles.logo} src={logoUrl} alt="login img" />
            </Typography>
            {!isXsScreen && <SwitchLanguageButton login />}
          </Box>

          <Box sx={styles.formCard}>
            <form onSubmit={handleFormSubmit} style={styles.formContainer} noValidate>
              <LoginStepIndicator activeIndex={stepperIndex} />

              {step === LOGIN_STEP.EMAIL && (
                <>
                  <Typography variant="h5" sx={styles.cardTitle}>
                    {t?.['Welcome back 👋']}
                  </Typography>
                  <Typography sx={styles.cardSubtitle}>
                    {t?.['Enter your work email to continue']}
                  </Typography>
                  {biometricCapability.available && (
                    <BiometricLoginButton
                      type={biometricCapability.type}
                      loading={biometricUnlockLoading}
                      onClick={handleBiometricLogin}
                      t={t}
                    />
                  )}
                  <OfoqTextField
                    customStyle={styles.textField}
                    defaultValue={email}
                    refState={(v) => onEmailChange(v)}
                    type="text"
                    required
                    name={t?.['Email Address']}
                    customPlaceHolder={t?.['Type Your Email Address']}
                    error={Boolean(emailSubmitError)}
                    helperText={emailSubmitError ?? undefined}
                    disabled={userAuth?.loading}
                  />

                  <OfoqThemeBtn
                    customStyle={styles.continueBtn}
                    name={
                      userAuth?.loading ? (
                        <CircularProgress size={20} sx={{ color: 'white' }} />
                      ) : (
                        t?.Continue
                      )
                    }
                    type="submit"
                    fullWidth
                  />
                </>
              )}

              {step === LOGIN_STEP.PASSWORD && (
                <>
                  <Button
                    type="button"
                    onClick={handleChangeEmail}
                    sx={styles.changeEmailLink}
                    disableRipple>
                    {t?.['Change email']}
                  </Button>
                  <Typography variant="h5" sx={styles.cardTitle}>
                    {t?.['Enter your password']}
                  </Typography>
                  <Typography sx={styles.cardSubtitle}>
                    {t?.['Welcome back! Please enter your password to continue.']}
                  </Typography>
                  <OfoqTextField
                    customStyle={styles.textField}
                    defaultValue={confirmedEmail}
                    refState={() => {}}
                    type="email"
                    name={t?.['Email Address']}
                    customPlaceHolder={t?.['Type Your Email Address']}
                    disabled
                    InputProps={{ readOnly: true }}
                    required
                    helperText={null}
                  />
                  <OfoqPasswordField
                    defaultValue={password}
                    refState={onPasswordChange}
                    required
                    placeholder={t?.['type your password']}
                    name={t?.Password}
                    hideInputValidator
                    autoFocus
                    error={Boolean(passwordSubmitError)}
                    helperText={passwordSubmitError}
                    customStyle={styles.passwordInput}
                  />
                  <Box sx={styles.passwordForgotContainer}>
                    <LinkBtn
                      customStyle={styles.forgotPassword}
                      to={AppRoutes.forgotPassword}
                      name={t?.['Forgot Your Password?']}
                    />
                  </Box>
                  <OfoqThemeBtn
                    customStyle={styles.continueBtn}
                    name={
                      userAuth?.loading ? (
                        <CircularProgress size={20} sx={{ color: 'white' }} />
                      ) : (
                        t?.Login
                      )
                    }
                    type="submit"
                    fullWidth
                  />
                </>
              )}
            </form>
          </Box>
        </Box>

        <Box sx={styles.rightImage}>
          <img src={LoginRightImage} alt="login img" />
        </Box>
        <Box sx={styles.backSvg}>
          <img src={LoginBackSvg} alt="login img" />
        </Box>
      </Box>

      <LoaderWithErrorHandler
        store={userAuth}
        resetAlertHandler={userAuthResetAlertHandler}
        disableSpinner
      />
    </Box>
  )
}

const styles = {
  root: {
    '& header': {
      marginBottom: '0px',
    },
  },
  loginContainer: {
    display: 'flex',
    justifyContent: 'space-around',
    alignItems: 'center',
    flexDirection: { xs: 'column-reverse', sm: 'row' },
    position: 'relative',
    minHeight: { xs: '70vh', sm: '90vh' },
    my: 'auto',
  },

  leftSideContainer: {
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    maxWidth: { xs: '100%', sm: '400px', md: '500px' },
    width: '100%',
    marginTop: { xs: '20px', sm: '0px' },
    mx: { xs: '0px', sm: '40px' },
    px: '10px',
    zIndex: '2',
  },
  formCard: {
    width: '100%',
    maxWidth: { xs: '100%', sm: '600px' },
    bgcolor: 'background.paper',
    borderRadius: '16px',
    boxShadow: '0px 8px 32px rgba(15, 23, 42, 0.08)',
    px: { xs: 2, sm: 3 },
    py: { xs: 2.5, sm: 3 },
    boxSizing: 'border-box',
  },
  logo: {
    width: '175px',
    height: '42px',
    objectFit: 'contain',
  },
  titleContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },

  formContainer: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },

  cardTitle: {
    fontWeight: 600,
    fontSize: { xs: '1.25rem', sm: '1.35rem' },
    color: 'text.primary',
    mb: 1,
    lineHeight: 1.3,
  },
  cardSubtitle: {
    color: 'text.secondary',
    fontSize: { xs: '0.875rem', sm: '0.9375rem' },
    mb: { xs: 2, sm: 2.5 },
    lineHeight: 1.5,
  },

  changeEmailLink: {
    alignSelf: 'flex-start',
    mb: 1.5,
    p: 0,
    minWidth: 0,
    textTransform: 'none',
    fontSize: { xs: '0.8125rem', sm: '0.875rem' },
    color: '#98a9bc',
    fontWeight: 400,
    '&:hover': {
      bgcolor: 'transparent',
      color: 'text.secondary',
    },
  },

  textField: {
    marginBottom: { xs: '10px', sm: '24px' },
    formInputLabelRequired: {
      fontSize: { xs: '10px', md: 'unset' },
    },
    '& .MuiFormHelperText-root': {
      display: 'flex !important',
    },
  },
  emailFieldWrap: {
    width: '100%',
  },
  emailSubmitError: {
    color: 'error.main',
    mt: 0.5,
    mx: 0.5,
    display: 'block',
    lineHeight: 1.4,
  },
  formTitle: {
    color: 'primary.contrastText',
    marginBottom: { xs: '30px', sm: '40px' },
    fontSize: { xs: '24px', sm: '28px' },
    textAlign: { xs: 'unset', md: 'left' },
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },

  forgotPassword: {
    maxWidth: '160px',
    textDecoration: 'unset',
    p: '0px !important',
    height: 'auto !important',
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'flex-end',
    color: '#98a9bc',
    backgroundColor: 'transparent !important',
    '&:hover': {
      color: '#98a9bc',
    },
  },
  continueBtn: {
    maxWidth: { xs: '100%', sm: '100%' },
    height: { xs: '40px', sm: '40px' },
    mt: '10px',
    ml: 0,
  },

  backSvg: {
    position: 'fixed',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 0,
    minHeight: '100vh',
    width: '100%',
  },
  rightImage: {
    position: 'relative',
    zIndex: '8',
    '& img': {
      width: { xs: '100%', sm: '300px', md: '400px', lg: '500px' },
      height: { xs: '200px', sm: '100%', md: '400px', lg: '500px' },
      mt: '20px',
    },
  },
  passwordForgotContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: '10px',
    mt: 0.5,
  },
  ssoBtnContainer: {
    display: 'none',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '1px',
    fontSize: { xs: '12px', sm: '14px' },
    textDecoration: 'underline',
    textDecorationColor: 'rgba(141, 143, 153, 1)',
    mt: '32px',
    fontWeight: 400,
    color: '#98a9bc',
  },
  ssoBtnLink: {
    textDecoration: 'underline',
    textDecorationColor: 'rgba(141, 143, 153, 1)',
    width: 'fit-content',
    minWidth: 'fit-content !important',
    p: '0px !important',
    pl: '3px !important',

    height: 'auto !important',
    backgroundColor: 'transparent !important',
    color: 'primary.main',
    '&:hover': {
      color: 'primary.main',
    },
  },
  passwordInput: {
    '& .MuiFormHelperText-root': {
      display: 'flex !important',
    },
  },
}

export default Index
